import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, QrCode, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ErrorState, LoadingState, Notice, PageHeader, Spinner, StatusBadge } from "@/components/common/States";
import { useAuth, useSpaceName } from "@/context/AuthContext";
import { api, errorMessage, fetcher } from "@/lib/api";
import { ACCESS_STATE, BILL_STATUS, rupiah } from "@/lib/labels";

// Halaman Langganan personal: hanya menampilkan tagihan akun sendiri untuk non-admin.
export default function SchoolLanggananSaya() {
  const space = useSpaceName();
  const { me, refresh } = useAuth();
  const qc = useQueryClient();
  // Status berubah karena tindakan reseller/founder di perangkat lain; jangan menunggu F5.
  const q = useQuery({
    queryKey: ["sekolah", "langganan-saya"],
    queryFn: () => fetcher("/school/langganan/saya"),
    refetchInterval: 15000,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const statusAkses = q.data?.bill ? `${q.data.bill.status}:${q.data.bill.access_state}` : "";
  const statusLalu = useRef("");
  useEffect(() => {
    if (!statusAkses) return;
    if (statusLalu.current && statusLalu.current !== statusAkses) refresh();
    statusLalu.current = statusAkses;
  }, [statusAkses, refresh]);

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  
  const d = q.data;
  const r = d.reseller;
  const bill = d.bill;
  const bisaKlaim = !!bill && bill.status === "PENDING";

  const bukaCheckout = () => {
    if (!bill || bill.status !== "PENDING") return;
    setCheckoutOpen(true);
  };

  const klaim = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/school/langganan/saya/sudah-bayar");
      toast.success("Akun Anda dilaporkan sudah dibayar. Reseller akan mengonfirmasi.");
      setCheckoutOpen(false);
      await qc.invalidateQueries({ queryKey: ["sekolah", "langganan-saya"] });
    } catch (ex) {
      const pesan = errorMessage(ex);
      setErr(pesan);
      toast.error(pesan);
      setCheckoutOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const kanal = r
    ? [r.qris_label && `QRIS ${r.qris_label}`, r.qris_nmid, r.bank_account && `Rekening ${r.bank_account}`]
        .filter(Boolean).join(" · ")
    : "";

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={space} title="Langganan Saya"
        description={`Tagihan langganan Anda periode ${d.periode}. ${r ? `Bayar ke ${r.name} — mitra yang melayani sekolah Anda.` : ""}`} />

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-4" data-testid="school-billing-saya-main">
          {err && <Notice tone="danger" testId="school-billing-saya-error">{err}</Notice>}
          {!r && <Notice tone="warn">Sekolah ini belum ditugaskan reseller aktif. Hubungi Pusat Kendali SIST.</Notice>}

          {!bill ? (
            <div className="rounded-2xl border bg-card p-8 text-center">
              <p className="text-sm text-muted-foreground">Tidak ada tagihan untuk akun Anda pada periode ini.</p>
            </div>
          ) : (
            <div className="rounded-2xl border bg-card">
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Status Pembayaran</p>
                    <StatusBadge map={BILL_STATUS} value={bill.status} />
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Harga</p>
                    <p className="text-2xl font-bold tabular-nums">{rupiah(bill.retail_price_idr)}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground mb-1">Akses Akun</p>
                  <StatusBadge map={ACCESS_STATE} value={bill.access_state} />
                </div>

                {bill.status === "PENDING" && bisaKlaim && (
                  <div className="border-t pt-4">
                    <Button onClick={bukaCheckout} disabled={busy} data-testid="school-billing-saya-claim-button">
                      {busy ? <Spinner /> : <BadgeCheck className="h-4 w-4 mr-1.5" />}
                      Bayar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <section className="rounded-2xl border bg-card p-5 space-y-3" data-testid="school-billing-saya-channel">
            <h2 className="flex items-center gap-2 text-base font-bold"><Wallet className="h-4 w-4 text-[#0B3D91]" aria-hidden /> Kanal Pembayaran</h2>
            {r ? (
              <>
                <p className="text-sm text-muted-foreground">Bayar langsung ke {r.name} — mitra yang melayani sekolah Anda.</p>
                <dl className="space-y-2 text-sm">
                  <div><dt className="text-xs text-muted-foreground">Harga per akun per bulan</dt>
                    <dd className="font-medium" data-testid="school-billing-saya-price">{rupiah(r.retail_price_idr)}</dd></div>
                  {kanal && <div><dt className="text-xs text-muted-foreground">Kanal</dt><dd className="font-medium break-words">{kanal}</dd></div>}
                </dl>
                {r.qris_image ? (
                  <div className="rounded-xl border p-4 grid place-items-center bg-muted/40" data-testid="school-billing-saya-qris">
                    <img src={r.qris_image} alt={`QRIS ${r.name}`} className="w-full max-w-64 rounded-lg" />
                  </div>
                ) : (
                  <div className="rounded-xl border p-4 grid place-items-center bg-muted/40 text-center" data-testid="school-billing-saya-qris-empty">
                    <QrCode className="h-6 w-6 text-muted-foreground mb-1" aria-hidden />
                    <p className="text-xs text-muted-foreground">{r.name} belum memasang gambar QRIS. Gunakan kanal di atas.</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Belum ada reseller aktif yang melayani sekolah ini.</p>
            )}
          </section>
        </aside>
      </div>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Konfirmasi Pembayaran</DialogTitle>
            <DialogDescription>
              Periksa ringkasan transaksi sebelum melaporkan pembayaran.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Akun</span>
                <span className="font-semibold">{me.user.full_name || me.user.email}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Harga</span>
                <span className="font-medium tabular-nums">{rupiah(bill?.retail_price_idr || 0)}</span>
              </div>
              <div className="border-t pt-3 flex justify-between items-center">
                <span className="font-semibold">Total pembayaran</span>
                <span className="text-xl font-bold text-[#E11D2E] tabular-nums">
                  {rupiah(bill?.retail_price_idr || 0)}
                </span>
              </div>
            </div>

            {r?.qris_image && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Scan QRIS untuk membayar:</p>
                <div className="rounded-xl border p-4 grid place-items-center bg-muted/40">
                  <img src={r.qris_image} alt={`QRIS ${r.name}`} className="w-full max-w-64 rounded-lg" />
                </div>
                {kanal && <p className="text-xs text-muted-foreground text-center">{kanal}</p>}
              </div>
            )}

            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
              <p className="font-medium mb-1">Setelah transfer:</p>
              <p>Tekan "Konfirmasi Sudah Bayar" untuk melaporkan ke {r?.name || "reseller"}. Status akan berubah menjadi "Menunggu konfirmasi".</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutOpen(false)} disabled={busy}>
              Batal
            </Button>
            <Button onClick={klaim} disabled={busy}>
              {busy ? <><Spinner /> Memproses...</> : <><BadgeCheck className="h-4 w-4 mr-1.5" /> Konfirmasi Sudah Bayar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
